import { createEntityQuery } from '@/data/createEntityQuery';
import { bitsEntity } from '@/data/entities/bits';
import { charactersEntity } from '@/data/entities/characters';
import { episodesEntity } from '@/data/entities/episodes';
import { featuresEntity } from '@/data/entities/features';
import { projectsEntity } from '@/data/entities/projects';
import { scenesEntity } from '@/data/entities/scenes';

// Query hooks created from entity definitions
const _useProject = createEntityQuery(projectsEntity, 'detail');
const _useEpisodePresentations = createEntityQuery(episodesEntity, 'presentations');
const _useScenePresentations = createEntityQuery(scenesEntity, 'presentations');
const _useEditorScenesTextAtoms = createEntityQuery(scenesEntity, 'textAtoms');
const _useScenesEpisodicPresentations = createEntityQuery(scenesEntity, 'episodicPresentations');
const _useCharacters = createEntityQuery(charactersEntity, 'list');

// Project
export const useProject = (projectId: number, version?: string, enabled = true) =>
    _useProject({ projectId, version }, { profile: 'editor', enabled });

// Episodes
export const useEpisodePresentations = (projectId: number, version?: string, enabled = true) =>
    _useEpisodePresentations({ projectId, version }, { profile: 'editor', enabled });

// Scenes
export const useScenePresentations = (
    args: {
        project_id: number;
        episode_id: number;
        version?: string;
        act?: number;
        tags?: string;
    },
    enabled = true,
) => _useScenePresentations(args, { profile: 'editor', enabled: enabled && !!args.episode_id });

export const useEditorScenesTextAtoms = (
    args: {
        project_id: number;
        episode_id: number;
        scenes: string;
        version?: string;
    },
    enabled = true,
) => _useEditorScenesTextAtoms(args, { profile: 'editor', enabled: enabled && !!args.episode_id && !!args.scenes });

export const useScenesEpisodicPresentations = (
    args: {
        project_id: number;
        episode_id: number;
        version?: string;
        tags?: string;
    },
    enabled = true,
) => _useScenesEpisodicPresentations(args, { profile: 'editor', enabled: enabled && !!args.episode_id });

// Characters — also re-exported here since it's used alongside scene queries
export const useCharacters = (projectId: number, version?: string, enabled = true) =>
    _useCharacters({ projectId, version }, { profile: 'editor', enabled });

// Bits
const _useBits = createEntityQuery(bitsEntity, 'list');

export const useBits = (episodeId: number, version?: string, enabled = true) =>
    _useBits({ episodeId, version }, { profile: 'editor', enabled: enabled && !!episodeId });

// Features
const _useGetFeatures = createEntityQuery(featuresEntity, 'list');

export const useGetFeatures = (projectId: number, version?: string, type?: number, enabled = true) =>
    _useGetFeatures({ projectId, version, type }, { profile: 'editor', enabled });
